import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  strapiGetLaboratoryWorkOrderForm,
  strapiSaveLaboratoryWorkOrder,
  type LaboratoryResultBeritaAcaraEntry,
} from '../../api/strapiCrm';
import type { StrapiManagedUserRow } from '../../api/strapiUsers';
import { useAuth } from '../../auth/AuthContext';
import { appDocumentTitle } from '../../appMeta';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useUsersQuery } from '../../query/hooks';
import { queryKeys } from '../../query/queryKeys';
import { groupBeritaAcaraByBidangAndTitik } from './laboratoryResultGrouping';
import { LaboratorySamplingFotoLink, LaboratorySamplingSnapshot } from './LaboratorySamplingSnapshot';
import {
  buildLaboratoryResultHash,
  parseLaboratoryResultHash,
  type LaboratoryResultTab,
} from './laboratoryResultRouteHash';
import { ChemParameterLabel } from '../../components/ChemInline';

function userLabel(u: StrapiManagedUserRow | null): string {
  if (!u) return '';
  return `${u.username || u.email}${u.numericId != null ? ` #${u.numericId}` : ''}`;
}

const BA_STATUS_LABEL: Record<string, string> = {
  pending: 'Belum diisi',
  draft: 'Draft',
  ready: 'Ready',
};

function initResultsByBa(
  beritaAcara: LaboratoryResultBeritaAcaraEntry[],
): Record<string, Record<string, string>> {
  const out: Record<string, Record<string, string>> = {};
  for (const ba of beritaAcara) {
    const perParam: Record<string, string> = {};
    for (const p of ba.parameters) {
      perParam[p.parameter_uji_document_id] = p.result ?? '';
    }
    out[ba.berita_acara_document_id] = perParam;
  }
  return out;
}

type ParameterGridRow = {
  id: string;
  berita_acara_document_id: string;
  parameter_uji_document_id: string;
  parameter_name: string;
  parameter_simbol: string;
  result: string;
};

function ParameterResultGrid({
  ba,
  resultsByBa,
  onResultChange,
}: {
  ba: LaboratoryResultBeritaAcaraEntry;
  resultsByBa: Record<string, Record<string, string>>;
  onResultChange: (baDocId: string, paramDocId: string, value: string) => void;
}) {
  const rows: ParameterGridRow[] = useMemo(
    () =>
      ba.parameters.map((p) => ({
        id: `${ba.berita_acara_document_id}:${p.parameter_uji_document_id}`,
        berita_acara_document_id: ba.berita_acara_document_id,
        parameter_uji_document_id: p.parameter_uji_document_id,
        parameter_name: p.parameter_name,
        parameter_simbol: p.parameter_simbol ?? '',
        result: resultsByBa[ba.berita_acara_document_id]?.[p.parameter_uji_document_id] ?? '',
      })),
    [ba, resultsByBa],
  );

  const columns: GridColDef<ParameterGridRow>[] = useMemo(
    () => [
      {
        field: 'parameter_name',
        headerName: 'Parameter',
        flex: 1,
        minWidth: 220,
        renderCell: ({ row }) => (
          <Box
            component="span"
            sx={{
              display: 'inline',
              py: 0.5,
              wordBreak: 'break-word',
              '& .katex-html': { whiteSpace: 'normal' },
              '& sub, & sup': { fontSize: '0.75em', lineHeight: 0 },
            }}
          >
            <ChemParameterLabel nama={row.parameter_name} simbol={row.parameter_simbol} />
          </Box>
        ),
      },
      {
        field: 'result',
        headerName: 'Hasil uji',
        flex: 1,
        minWidth: 200,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <TextField
            variant="standard"
            size="small"
            fullWidth
            placeholder="Isi hasil"
            value={row.result}
            onChange={(e) =>
              onResultChange(
                row.berita_acara_document_id,
                row.parameter_uji_document_id,
                e.target.value,
              )
            }
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            sx={{ py: 0.5 }}
          />
        ),
      },
    ],
    [onResultChange],
  );

  return (
    <DataGrid
      rows={rows}
      columns={columns}
      disableRowSelectionOnClick
      hideFooter={rows.length <= 15}
      autoHeight
      density="compact"
      getRowHeight={() => 'auto'}
      sx={{
        width: '100%',
        '& .MuiDataGrid-columnHeaders': { fontWeight: 600 },
        '& .MuiDataGrid-cell': { alignItems: 'center', py: 0.25 },
      }}
    />
  );
}

export function LaboratoryResultDetailRoute() {
  const { jwt, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const { workOrderDocumentId } = useParams<{ workOrderDocumentId: string }>();
  const woDocId = workOrderDocumentId?.trim() ?? '';
  const tab = useMemo(() => parseLaboratoryResultHash(location.hash), [location.hash]);

  const [resultsByBa, setResultsByBa] = useState<Record<string, Record<string, string>>>({});
  const [approvedBy, setApprovedBy] = useState<StrapiManagedUserRow | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);

  useDocumentTitle(appDocumentTitle('Hasil uji lab — isi hasil'));

  const formQ = useQuery({
    queryKey: queryKeys.laboratoryResultWorkOrderForm(jwt, woDocId),
    queryFn: () => strapiGetLaboratoryWorkOrderForm(jwt!, woDocId),
    enabled: Boolean(jwt && woDocId),
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const usersQ = useUsersQuery(jwt, { emailContains: '', status: 'active' });
  const userOptions = useMemo(
    () => (usersQ.data?.rows ?? []).filter((u) => u.numericId != null),
    [usersQ.data?.rows],
  );

  React.useEffect(() => {
    if (!formQ.data) return;
    setResultsByBa(initResultsByBa(formQ.data.berita_acara));
    const approvedId = formQ.data.approved_by_user_id;
    const matchApproved =
      approvedId != null ? userOptions.find((u) => u.numericId === approvedId) : null;
    setApprovedBy(
      matchApproved ?? userOptions.find((u) => u.numericId === user?.id) ?? userOptions[0] ?? null,
    );
  }, [formQ.data, userOptions, user?.id]);

  useEffect(() => {
    const expected = buildLaboratoryResultHash(parseLaboratoryResultHash(location.hash));
    if (location.hash === expected) return;
    navigate(
      { pathname: location.pathname, search: location.search, hash: expected },
      { replace: true },
    );
  }, [location.hash, location.pathname, location.search, navigate]);

  function goTab(next: LaboratoryResultTab) {
    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: buildLaboratoryResultHash(next),
      },
      { replace: true },
    );
  }

  const buildSavePayload = useCallback(() => {
    if (!formQ.data) return null;
    const berita_acara = formQ.data.berita_acara.map((ba) => ({
      berita_acara_document_id: ba.berita_acara_document_id,
      items: ba.parameters.map((p) => ({
        parameter_uji_document_id: p.parameter_uji_document_id,
        result: (resultsByBa[ba.berita_acara_document_id]?.[p.parameter_uji_document_id] ?? '').trim(),
      })),
    }));
    return { berita_acara };
  }, [formQ.data, resultsByBa]);

  const saveM = useMutation({
    mutationFn: async (status: 'draft' | 'ready') => {
      if (!jwt || !woDocId || !formQ.data) throw new Error('Data tidak lengkap.');
      if (status === 'ready' && approvedBy?.numericId == null) {
        throw new Error('Pilih Approved by untuk simpan dan complete.');
      }
      const base = buildSavePayload();
      if (!base) throw new Error('Data tidak lengkap.');
      return strapiSaveLaboratoryWorkOrder(jwt, woDocId, {
        status,
        ...(approvedBy?.numericId != null
          ? { approved_by_user_id: approvedBy.numericId }
          : {}),
        ...base,
      });
    },
    onSuccess: async (data) => {
      setSaveError(null);
      await qc.invalidateQueries({ queryKey: queryKeys.laboratoryResultGridRowsPrefix(jwt) });
      await qc.invalidateQueries({ queryKey: queryKeys.laboratoryResultWorkOrderForm(jwt, woDocId) });
      if (data.status === 'ready') {
        setSaveSuccess(null);
        navigate('/sampling/laboratory-results');
      } else {
        setSaveSuccess('Hasil tersimpan (draft). Preview LHU muncul setelah Simpan dan complete.');
      }
    },
    onError: (e) => {
      setSaveSuccess(null);
      setSaveError(e instanceof Error ? e.message : 'Gagal menyimpan.');
    },
  });

  const form = formQ.data;

  const bidangGroups = useMemo(
    () => (form ? groupBeritaAcaraByBidangAndTitik(form.berita_acara) : []),
    [form],
  );

  const handleResultChange = useCallback((baDocId: string, paramDocId: string, value: string) => {
    setResultsByBa((prev) => ({
      ...prev,
      [baDocId]: {
        ...(prev[baDocId] ?? {}),
        [paramDocId]: value,
      },
    }));
  }, []);

  if (formQ.isPending) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (formQ.isError || !form) {
    return (
      <Alert severity="error">
        {formQ.error instanceof Error ? formQ.error.message : 'Gagal memuat form.'}
      </Alert>
    );
  }

  const woTitle = form.work_order_job_number || form.work_order_label || woDocId;

  return (
    <Box sx={{ maxWidth: tab === 'parameter-hasil-uji' ? '100%' : 840, width: '100%' }}>
      <Button
        component={RouterLink}
        to="/sampling/laboratory-results"
        startIcon={<ArrowBackIcon />}
        sx={{ mb: 2 }}
      >
        Kembali ke daftar
      </Button>

      <Typography variant="h1" component="h1" sx={{ fontSize: '1.35rem', fontWeight: 600, mb: 1 }}>
        {woTitle}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {form.work_order_label} · {form.berita_acara.length} berita acara
      </Typography>

      {form.status === 'ready' ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          Status <strong>ready</strong> — hasil uji masuk antrian Verifikasi SPV di menu Invoice.
          Preview LHU aktif.
        </Alert>
      ) : form.status === 'draft' ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Status <strong>draft</strong> — simpan hasil uji; Preview aktif setelah Simpan dan complete.
        </Alert>
      ) : null}

      {saveError ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>
          {saveError}
        </Alert>
      ) : null}
      {saveSuccess ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaveSuccess(null)}>
          {saveSuccess}
        </Alert>
      ) : null}

      <Tabs value={tab} onChange={(_, v: LaboratoryResultTab) => goTab(v)} sx={{ mb: 2 }}>
        <Tab value="detail" label="Detail" />
        <Tab value="parameter-hasil-uji" label="Parameter & hasil uji" />
      </Tabs>

      {tab === 'detail' ? (
        <Stack spacing={2}>
          <TextField variant="standard" label="GL job / Work order" size="small" fullWidth value={woTitle} disabled />
          <TextField variant="standard" label="Label work order" size="small" fullWidth value={form.work_order_label} disabled />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, pt: 1 }}>
            Daftar berita acara (per bidang pengujian)
          </Typography>
          {bidangGroups.map((group) => (
            <Box key={group.bidangLabel}>
              <Typography variant="h6" component="h2" sx={{ fontSize: '1.05rem', fontWeight: 600, mb: 1.5 }}>
                Bidang pengujian: {group.bidangLabel}
              </Typography>
              <Stack spacing={1.5}>
                {group.titikList.map((ba) => (
                  <Paper key={ba.berita_acara_document_id} variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {ba.row_label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Status: {BA_STATUS_LABEL[ba.status] ?? ba.status} · {ba.parameters.length}{' '}
                      parameter
                    </Typography>
                    <Typography variant="body2">{ba.location_name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {ba.location_address}
                    </Typography>
                    <LaboratorySamplingFotoLink fotoUrl={ba.sampling?.foto_url} />
                  </Paper>
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>
      ) : (
        <Stack spacing={3}>
          <Typography variant="body2" color="text.secondary">
            Dikelompokkan per bidang pengujian, lalu titik. Isian petugas sampling tampil di atas grid.
            Isi kolom <strong>Hasil uji</strong> per parameter.
          </Typography>

          {bidangGroups.map((group) => (
            <Box key={group.bidangLabel}>
              <Typography variant="h6" component="h2" sx={{ fontSize: '1.05rem', fontWeight: 600, mb: 1.5 }}>
                Bidang pengujian: {group.bidangLabel}
              </Typography>
              <Stack spacing={2.5}>
                {group.titikList.map((ba) => (
                  <Box key={ba.berita_acara_document_id}>
                    <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 0.75 }} flexWrap="wrap" useFlexGap>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {ba.point_label}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {BA_STATUS_LABEL[ba.status] ?? ba.status}
                        {ba.location_name ? ` · ${ba.location_name}` : ''}
                      </Typography>
                      <LaboratorySamplingFotoLink
                        fotoUrl={ba.sampling?.foto_url}
                        testId={`laboratory-result-foto-link-${ba.berita_acara_document_id}`}
                      >
                        Foto
                      </LaboratorySamplingFotoLink>
                    </Stack>
                    <LaboratorySamplingSnapshot sampling={ba.sampling} />
                    <ParameterResultGrid
                      ba={ba}
                      resultsByBa={resultsByBa}
                      onResultChange={handleResultChange}
                    />
                  </Box>
                ))}
              </Stack>
            </Box>
          ))}

          <Divider />

          <Autocomplete
            options={userOptions}
            value={approvedBy}
            onChange={(_, v) => setApprovedBy(v)}
            getOptionLabel={userLabel}
            isOptionEqualToValue={(a, b) => a.numericId === b.numericId}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="standard"
                label="Approved by (wajib untuk Simpan dan complete)"
                size="small"
              />
            )}
          />
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              variant="outlined"
              disabled={saveM.isPending}
              onClick={() => {
                setSaveError(null);
                setSaveSuccess(null);
                saveM.mutate('draft');
              }}
            >
              {saveM.isPending ? 'Menyimpan…' : 'Simpan'}
            </Button>
            <Button
              variant="contained"
              disabled={saveM.isPending || approvedBy?.numericId == null}
              onClick={() => {
                setSaveError(null);
                setSaveSuccess(null);
                saveM.mutate('ready');
              }}
            >
              {saveM.isPending ? 'Menyimpan…' : 'Simpan dan complete'}
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary" display="block">
            Simpan = status draft (tombol Preview di daftar belum aktif). Simpan dan complete = status
            ready, lalu SPV review di menu Invoice (status Verifikasi).
          </Typography>
        </Stack>
      )}
    </Box>
  );
}
